import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { gstNumberRegex } from "utils/Regex";
import CommonGoogleAutoComplete from "components/commonGoogleAutoComplete/CommonGoogleAutoComplete";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_ENDPOINT } from "services/Api";

let env = process.env.REACT_APP_B2B_API_BASE_URL;

const requestInstance = axios.create({
  baseURL: env,
});

const CustomBranchFields = ({
  dynamicInputs,
  ele,
  register,
  setValue,
  clearErrors,
  setError,
  errors,
  propsData,
  setDynamicInputs,
  isDisabled,
  companyAddress,
  onGarageLocationSearch,
  garageLocationOptions = [],
  garageLocationLoader = false,
}) => {
  // State management

  const [garageSearchInitiated, setGarageSearchInitiated] = useState({});
  const [userHasTyped, setUserHasTyped] = useState({});

  const dispatch = useDispatch();

  //function for adding dynamic fields
  const handleAddInput = (index) => {
    const newObj = {
      [`city-${index}`]: "",
      [`address-${index}`]: "",
      [`state-${index}`]: "",
      [`gstNumber-${index}`]: "",
      [`sez-${index}`]: "",
      [`garageLocation-${index}`]: "",
      [`garageAddress-${index}`]: "",
    };
    setDynamicInputs([...dynamicInputs, newObj]);
  };

  //function for delete dynamic input fields
  const handleDeleteInput = (index) => {
    const newArray = [...dynamicInputs];
    newArray.splice(index - 1, 1);
    setDynamicInputs(newArray);

    // Clean up all garage-related states for deleted index
    const newGarageSearchInitiated = { ...garageSearchInitiated };
    const newUserHasTyped = { ...userHasTyped };

    delete newGarageSearchInitiated[`garageLocation-${index}`];
    delete newUserHasTyped[`garageLocation-${index}`];

    setGarageSearchInitiated(newGarageSearchInitiated);
    setUserHasTyped(newUserHasTyped);
  };

  // function for handle change dynamic field
  const handleDynaminInputChange = (event, index) => {
    let { name, value } = event.target;
    let onChangeValue = [...dynamicInputs];

    onChangeValue[index][name] = name.includes("gstNumber")
      ? value.toUpperCase()
      : value;

    if (name.includes("gstNumber")) {
      setValue(name, value.toUpperCase());
    }

    setDynamicInputs(onChangeValue);

    if (name.includes("city")) {
      const stateData = propsData?.cityList.filter((obj) => obj?.id === value);
      setValue(`state-${index}`, stateData?.[0]?.state);
      clearErrors(`state-${index}`);
      let onChangeValue = [...dynamicInputs];
      onChangeValue[index][`state-${index}`] = stateData?.[0]?.state;
      setDynamicInputs(onChangeValue);
    } else if (name.includes("gstNumber")) {
      if (value?.length === 15 && gstNumberRegex.test(value)) {
        clearErrors(name);
      } else {
        setError(name);
      }
    } else if (name.includes("sez")) {
      onChangeValue[index][name] = event.target.checked;
      setDynamicInputs(onChangeValue);
    } else if (name.includes("garageLocation")) {
      clearErrors(name);
    } else {
      clearErrors(name);
    }
  };

  // Handle garage location selection
  const handleGarageLocationSelect = async (event, selectedOption, index) => {
    if (selectedOption) {
      let url = `${env}${API_ENDPOINT.GoogleMap.placeApi}?placeId=${selectedOption?.place_id}`;
      const data = await requestInstance.get(url).catch((err) => err.response);
      const coordinates = data?.data?.data?.result?.geometry?.location;

      const address =
        selectedOption.formatted_address ||
        selectedOption.description ||
        selectedOption.name ||
        "";

      let onChangeValue = [...dynamicInputs];
      onChangeValue[index][`garageLocation-${index}`] = [
        coordinates?.lat,
        coordinates?.lng,
      ];
      onChangeValue[index][`garageAddress-${index}`] = address;
      setDynamicInputs(onChangeValue);

      setValue(`garageLocation-${index}`, coordinates);
      setValue(`garageAddress-${index}`, address);
      clearErrors(`garageLocation-${index}`);

      // Only reset search initiated state
      setGarageSearchInitiated({
        ...garageSearchInitiated,
        [`garageLocation-${index}`]: false,
      });
    }
  };

  return (
    <Grid className="mb20">
      {dynamicInputs?.map((data, index) => (
        <Box key={index}>
          <Box className="mb10">
            <Typography className="fw600 colorPrimary" variant="body1">
              {index === 0
                ? "Head Office Details"
                : `Branch ${index} Office Address`}
            </Typography>
          </Box>
          <Grid
            container
            spacing={{ md: 2 }}
            columns={{ xs: 1, sm: 3, md: 12 }}
            key={index}
            className="mb20"
          >
            <Grid item xs={12} md={6}>
              <Typography variant="body2" className="fw600 colorBlack mb20">
                {ele?.label} City
                <span className="colorRed">*</span>
              </Typography>
              <Box className="customSelectOption">
                <Select
                  defaultValue={
                    !data[`city-${index}`]?.length > 0
                      ? ele?.menuOptions[0]?.id
                      : data[`city-${index}`]
                  }
                  IconComponent={(params) => (
                    <ExpandMoreRoundedIcon className="mr10" {...params} />
                  )}
                  className="width100"
                  {...register(`city-${index}`, {
                    required: true,
                  })}
                  onChange={(event) => handleDynaminInputChange(event, index)}
                  displayEmpty
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                >
                  <InputLabel disabled className="colorGrey ml16">
                    <Typography className="colorGrey" variant="body2">
                      {ele?.placeholder}
                    </Typography>
                  </InputLabel>
                  {ele?.menuOptions?.map((option, idx) => (
                    <MenuItem key={idx} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" className="fw600 colorBlack mb20">
                Address <span className="colorRed">*</span>
              </Typography>
              <Box className="customTextField">
                <TextField
                  name={`address-${index}`}
                  placeholder="Enter Address"
                  {...register(`address-${index}`, {
                    required: true,
                  })}
                  type="text"
                  onChange={(event) => handleDynaminInputChange(event, index)}
                  disabled={isDisabled}
                  multiline={ele?.multiline}
                />
                {errors[`address-${index}`] && (
                  <Typography variant="body2" className="errorMessage">
                    This field is required
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" className="fw600 colorBlack mb20">
                State <span className="colorRed">*</span>
              </Typography>
              <Box className="customTextField">
                <TextField
                  name={`state-${index}`}
                  placeholder="Enter State"
                  {...register(`state-${index}`, {
                    required: true,
                  })}
                  type="text"
                  disabled={true}
                  multiline={ele?.multiline}
                />
                {errors[`state-${index}`] && (
                  <Typography variant="body2" className="errorMessage">
                    This field is required
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" className="fw600 colorBlack mb20">
                GST Number
              </Typography>
              <Box className="customTextField">
                <TextField
                  className="textCapital"
                  name={`gstNumber-${index}`}
                  placeholder="Enter GST Number"
                  {...register(`gstNumber-${index}`, {
                    required: false,
                  })}
                  type="text"
                  onChange={(event) => handleDynaminInputChange(event, index)}
                  disabled={isDisabled}
                  multiline={ele?.multiline}
                />
                {errors[`gstNumber-${index}`] && (
                  <Typography variant="body2" className="errorMessage">
                    Enter a Valid GST Number
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" className="fw600 colorBlack mb20">
                Garage Location <span className="colorRed">*</span>
              </Typography>
              <CommonGoogleAutoComplete
                defaultValue={data[`garageAddress-${index}`] || ""}
                getOptionLabel={(option) =>
                  option?.structured_formatting?.main_text ||
                  option?.formatted_address ||
                  option?.description ||
                  option?.name ||
                  option
                }
                disabled={isDisabled}
                // Show options only if user has manually typed AND we have search results
                options={
                  userHasTyped[`garageLocation-${index}`] &&
                  garageSearchInitiated[`garageLocation-${index}`]
                    ? garageLocationOptions
                    : []
                }
                onHandleChange={(event, selectedOption) => {
                  handleGarageLocationSelect(event, selectedOption, index);
                  // Clear search state after selection
                  setGarageSearchInitiated({
                    ...garageSearchInitiated,
                    [`garageLocation-${index}`]: false,
                  });
                  // Keep userHasTyped as true so they can search again
                }}
                onInputChange={(event, value) => {
                  // Mark that user has started typing (not just prefilled)
                  if (event?.type === "change") {
                    setUserHasTyped({
                      ...userHasTyped,
                      [`garageLocation-${index}`]: true,
                    });
                  }

                  // Search logic
                  if (
                    value &&
                    value.length > 2 &&
                    userHasTyped[`garageLocation-${index}`]
                  ) {
                    setGarageSearchInitiated({
                      ...garageSearchInitiated,
                      [`garageLocation-${index}`]: true,
                    });

                    if (onGarageLocationSearch) {
                      const syntheticEvent = {
                        type: "change",
                        target: { name: `garageLocation-${index}` },
                      };
                      onGarageLocationSearch(value, syntheticEvent);
                    }
                  } else if (!value || value.length <= 2) {
                    // Clear search when input is cleared or too short
                    setGarageSearchInitiated({
                      ...garageSearchInitiated,
                      [`garageLocation-${index}`]: false,
                    });
                  }
                }}
                ele={{
                  name: `garageLocation-${index}`,
                  placeholder: "Search garage location...",
                  required: true,
                  clearOnBlur: false,
                }}
                register={register}
                pickupLandmarkRequired={{}}
                fieldType={`garageLocation-${index}`}
                fieldLoader={garageLocationLoader}
              />
              {errors[`garageLocation-${index}`] && (
                <Typography color="error">
                  Garage location is required
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <FormGroup className="filterBox flex flexRow">
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      value={data[`sez-${index}`]}
                      checked={data[`sez-${index}`]}
                      disabled={
                        !gstNumberRegex.test(data[`gstNumber-${index}`])
                      }
                      id={`sez-${index}`}
                      name={`sez-${index}`}
                      onChange={(event) =>
                        handleDynaminInputChange(event, index)
                      }
                    />
                  }
                  label={
                    <Typography
                      variant={"head5"}
                      className={`colorBlack fw400 ${
                        !gstNumberRegex.test(data[`gstNumber-${index}`]) &&
                        "colorGrey"
                      }`}
                    >
                      SEZ( Special Economy Zone )
                    </Typography>
                  }
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Box>
      ))}

      <Box className="justifyEnd">
        <Box>
          <Typography
            variant="body2"
            className="fw400 pt20 justifyCenter"
            role="presentation"
            onClick={() => handleAddInput(dynamicInputs?.length)}
          >
            <Typography variant="head4" className="colorBlue pr10">
              +
            </Typography>
            Add Branch
          </Typography>
        </Box>
        <Box className="ml20">
          {dynamicInputs?.length > 1 &&
            (!companyAddress ||
              dynamicInputs.length > companyAddress.length) && (
              <Typography
                variant="body2"
                className="fw400 pt20 alignItem onBoardDeleteBtn"
                role="presentation"
                onClick={() => handleDeleteInput(dynamicInputs?.length)}
              >
                <span className="pr10 ">
                  <DeleteOutlinedIcon role="presentation" />
                </span>
                Delete
              </Typography>
            )}
        </Box>
      </Box>
    </Grid>
  );
};

export default CustomBranchFields;
